import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import {
  type BusinessProfile,
  type ParsedStripeErrors,
  formatEpoch,
} from "../types";
import { connectedAccountUrl, customerUrl } from "@/lib/stripe-links";
import { OverlayRow } from "./OverlaySection";

interface StripeIntegrationCardProps {
  business: BusinessProfile;
  stripeErrors?: ParsedStripeErrors | null;
}

/** Rendered inside `OverlaySection`, which owns the heading and the shell. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return <OverlayRow label={label}>{children}</OverlayRow>;
}

function StripeLink({
  id,
  type,
}: {
  id: string;
  type: "connect/accounts" | "customers";
}) {
  const url =
    type === "connect/accounts" ? connectedAccountUrl(id) : customerUrl(id);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-xs break-all text-blue-600 hover:text-blue-800 hover:underline"
    >
      {id}
    </a>
  );
}

export function StripeIntegrationCard({
  business,
  stripeErrors,
}: StripeIntegrationCardProps) {
  const payoutInterval = business.payoutState?.interval ?? null;
  const currentlyDue = stripeErrors?.currentlyDue ?? [];
  const restrictedCapabilities = stripeErrors?.restrictedCapabilities ?? [];
  const hasIssues =
    currentlyDue.length > 0 || restrictedCapabilities.length > 0;

  return (
    <div>
      <div>
        <Field label="Connected Account">
          {business.stripeConnectedAccountId ? (
            <StripeLink
              id={business.stripeConnectedAccountId}
              type="connect/accounts"
            />
          ) : (
            <span className="text-subtle-foreground">—</span>
          )}
        </Field>

        <Field label="Customer ID">
          {business.stripeCustomerId ? (
            <StripeLink id={business.stripeCustomerId} type="customers" />
          ) : (
            <span className="text-subtle-foreground">—</span>
          )}
        </Field>

        <Field label="Charges Enabled">
          {business.stripeChargesEnabled === true ? (
            <span className="font-medium text-green-700">Yes</span>
          ) : business.stripeChargesEnabled === false ? (
            <span className="font-medium text-red-600">No</span>
          ) : (
            <span className="text-subtle-foreground">—</span>
          )}
        </Field>

        <Field label="Payout Interval">
          {payoutInterval ? (
            <span className="capitalize">{payoutInterval}</span>
          ) : (
            <span className="text-subtle-foreground">—</span>
          )}
        </Field>
      </div>

      {hasIssues && (
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2.5">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            Outstanding Requirements
            {stripeErrors?.currentDeadlineStamp != null && (
              <span className="ml-auto font-normal text-amber-600">
                Deadline: {formatEpoch(stripeErrors.currentDeadlineStamp)}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {currentlyDue.map((item) => (
              <span
                key={item}
                className="rounded bg-yellow-100 px-2 py-0.5 font-mono text-xs text-amber-800"
              >
                {item}
              </span>
            ))}
            {restrictedCapabilities.map((cap) => (
              <span
                key={cap}
                className="rounded bg-red-100 px-2 py-0.5 font-mono text-xs text-red-700"
              >
                {cap}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
